import React from 'react';
import { List, Card, Tag, Typography, Space, Button, Empty, Spin, Divider } from 'antd';
import { CalendarOutlined, TrophyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { ParticipantCycleHistorySummary } from '../../types/engagement.types';

dayjs.locale('pt-br');

const { Text } = Typography;

const PRIZE_LABELS: Record<number, string> = { 1: '1º', 2: '2º', 3: '3º', 4: '4º', 5: '5º' };

interface CycleHistorySectionProps {
  cycles: ParticipantCycleHistorySummary[];
  loading?: boolean;
  onViewResults?: (cycleKey: string) => void;
  onViewTickets?: (cycleKey: string) => void;
}

export const CycleHistorySection: React.FC<CycleHistorySectionProps> = ({
  cycles,
  loading,
  onViewResults,
  onViewTickets,
}) => {
  const getPrizes = (cycle: ParticipantCycleHistorySummary) => cycle.prizes ?? [];

  if (loading) return <Spin size="small" />;

  if (cycles.length === 0) {
    return <Empty description="Nenhum ciclo encerrado ainda" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <List
      dataSource={cycles}
      itemLayout="vertical"
      renderItem={(cycle: ParticipantCycleHistorySummary) => (
        <List.Item key={cycle.cycleKey}>
          <Card size="small" bordered={false} style={{ background: '#fafafa' }}>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>

              {/* Cabeçalho */}
              <Space>
                <Text strong style={{ fontSize: 15 }}>Ciclo {cycle.cycleKey}</Text>
                <Tag color="default">Encerrado</Tag>
              </Space>

              {/* Período */}
              <Text type="secondary">
                <CalendarOutlined style={{ marginRight: 4 }} />
                {dayjs(cycle.startsAt).format('DD/MM/YYYY')} a {dayjs(cycle.endsAt).format('DD/MM/YYYY')}
              </Text>

              {/* Pontuação por ação */}
              <Text type="secondary">
                {cycle.surveyPoints} pt{cycle.surveyPoints !== 1 ? 's' : ''} por pesquisa
                {' · '}
                {cycle.referralPoints} pt{cycle.referralPoints !== 1 ? 's' : ''} por indicação
              </Text>

              {/* Bilhetes */}
              <Space wrap>
                {cycle.ticketSummary.valid > 0 && (
                  <Tag color="success">{cycle.ticketSummary.valid} bilhete{cycle.ticketSummary.valid !== 1 ? 's' : ''} válido{cycle.ticketSummary.valid !== 1 ? 's' : ''}</Tag>
                )}
                {cycle.ticketSummary.pending > 0 && (
                  <Tag color="warning">{cycle.ticketSummary.pending} pendente{cycle.ticketSummary.pending !== 1 ? 's' : ''}</Tag>
                )}
                {cycle.ticketSummary.invalid > 0 && (
                  <Tag color="error">{cycle.ticketSummary.invalid} inválido{cycle.ticketSummary.invalid !== 1 ? 's' : ''}</Tag>
                )}
                {cycle.ticketSummary.valid === 0 && cycle.ticketSummary.pending === 0 && cycle.ticketSummary.invalid === 0 && (
                  <Text type="secondary">Sem bilhetes</Text>
                )}
              </Space>

              {/* Prêmios */}
              {getPrizes(cycle).length > 0 && (
                <>
                  <Divider style={{ margin: '4px 0' }} />
                  <Space wrap>
                    <TrophyOutlined style={{ color: '#faad14' }} />
                    {getPrizes(cycle).map((p) => (
                      <Text key={p.prizeRank} type="secondary">
                        {PRIZE_LABELS[p.prizeRank] ?? `${p.prizeRank}º`} {p.prizeValue}
                      </Text>
                    ))}
                  </Space>
                </>
              )}

              {/* Sorteio + SPA */}
              {(cycle.lotteryDrawDate || cycle.lotteryDrawNumber || cycle.spaRegistrationNumber) && (
                <Space direction="vertical" size={2}>
                  {(cycle.lotteryDrawDate || cycle.lotteryDrawNumber) && (
                    <Text type="secondary">
                      Sorteio Loteria Federal
                      {cycle.lotteryDrawNumber ? ` — Concurso nº ${cycle.lotteryDrawNumber}` : ''}
                      {cycle.lotteryDrawDate ? ` em ${dayjs(cycle.lotteryDrawDate).format('DD/MM/YYYY')}` : ''}
                    </Text>
                  )}
                  {cycle.spaRegistrationNumber && (
                    <Text type="secondary">Registro SPA: {cycle.spaRegistrationNumber}</Text>
                  )}
                </Space>
              )}

              {/* Ações */}
              <Space>
                {onViewTickets && (
                  <Button size="small" onClick={() => onViewTickets(cycle.cycleKey)}>
                    Ver bilhetes
                  </Button>
                )}
                {cycle.hasResults && onViewResults && (
                  <Button size="small" type="primary" ghost onClick={() => onViewResults(cycle.cycleKey)}>
                    Ver resultados
                  </Button>
                )}
              </Space>

            </Space>
          </Card>
        </List.Item>
      )}
    />
  );
};

export default CycleHistorySection;
